import React from 'react';
import {
  CartesianGrid,
  Dot,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface DetailMetricsChartProps {
  data: number[];
}

const formatCompact = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return `${Math.round(value)}`;
};

const DetailMetricsChart: React.FC<DetailMetricsChartProps> = ({ data }) => {
  const safe = data.length > 0 ? data : [0, 0, 0, 0, 0, 0];
  const points = safe.map((value, index) => ({
    value,
    label: `${Math.max((safe.length - index - 1) * 4 + 2, 2)} h ago`,
  }));

  const maxValue = Math.max(...safe, 0);
  const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.25) : 10;

  return (
    <div style={{ width: '100%', height: 235 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 12, right: 6, bottom: 14, left: 10 }}>
          <CartesianGrid stroke="#e7edf6" vertical={false} strokeDasharray="2 3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#6c7b97', fontSize: 12 }}
            dy={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#6c7b97', fontSize: 12 }}
            domain={[0, yMax]}
            ticks={[0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax].map((value) => Math.round(value))}
            tickFormatter={formatCompact}
            width={48}
          />
          <Tooltip
            formatter={(value: number) => [formatCompact(value), 'Events']}
            contentStyle={{
              border: '1px solid #d5deec',
              borderRadius: 8,
              boxShadow: '0 4px 14px rgba(13, 36, 69, 0.12)',
              fontSize: 12,
            }}
            labelStyle={{ color: '#506482' }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#586987"
            strokeWidth={3}
            dot={<Dot r={3} fill="#5f708f" stroke="#ffffff" strokeWidth={2} />}
            activeDot={{ r: 5, fill: '#2f5be7' }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DetailMetricsChart;
